var SliderArranger,
    kind   = require("enyo/kind"),
    logger = require("enyo/logger"),
    utils  = require("enyo/utils");

require("enyo-ui");

/**
 * An abstract arranger for [Slider]{@link enyo-ui.Slider} controls. It defines
 * some required methods and some common functionality.
 *
 * This class is not meant to be used directly, but rather to be extended to
 * implement particular transitions for slides.
 *
 * @ui
 * @class enyo-ui.SliderArranger
 * @abstract
 * @public
 */
SliderArranger = module.exports = kind(/** @lends  enyo-ui.SliderArranger.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.SliderArranger",

    /**
     * @private
     */
    kind: null,

    /**
     * The slider that's currently using this arranger.
     *
     * @type {?enyo-ui.Slider}
     * @private
     */
    slider: null,

    /**
     * Default options for the arranger.
     *
     * @type {Object}
     * @protected
     */
    options: {

        /**
         * @type {enyo-ui.SlideTransition}
         * @public
         */
        transitionKind: null,

        /**
         * Optionally leave some extra options to tweak the animation.
         *
         * @type {?Object}
         * @public
         */
        transitionOptions: {
            friction:   0.75,
            attraction: 0.025
        }
    },

    /**
     * The width of the slideContainer.
     *
     * @type    {number}
     * @default 0
     * @readonly
     * @public
     */
    containerWidth: 0,

    /**
     * The height of the slideContainer.
     *
     * @type    {number}
     * @default 0
     * @readonly
     * @public
     */
    containerHeight: 0,

    /**
     * Sets reference to slider and imports arrangerOptions from slider.
     *
     * @param {enyo.Control}  slider  The slider
     * @private
     */
    constructor: function(slider) {
        this.slider = slider;

        if (slider) {
            this.options = utils.mixin(utils.clone(this.options || {}), slider.arrangerOptions);
        }
    },

    /**
     * Convenience method for retrieving options, automatically resolves
     * computed options.
     *
     * @param  {string}  option  The option
     * @return {*}       The option's property on the slider as soon as the
     *                   slide is active.
     * @public
     */
    getOption: function(option) {
        var value;

        value = this.options[option];

        return utils.isFunction(value) ? value.call(this) : value;
    },

    /**
     * Gets the dimensions of the slide container, allowing proper alignment of
     * the slides..
     *
     * @private
     */
    reflow: function() {
        var slider = this.slider,
            containerBounds,
            slides = slider.recordUidToSlide,
            slide;

        // Memorize container bounds
        containerBounds      = slider.getSlideContainer().getBounds();
        this.containerWidth  = containerBounds.width;
        this.containerHeight = containerBounds.height;

        // Arrange the slides
        for (slide in slides) {
            if (slides.hasOwnProperty(slide)) {
                this.arrangeSlide(slides[slide]);
            }
        }
    },

    /**
     * Creates a SliderTransition instance for the slide.
     *
     * @param  {enyo.Control}              slide  The slide
     * @return {enyo-ui.SliderTransition}  The instantiated transition
     * @public
     */
    createTransition: function(slide) {
        var Transition = this.getOption("transitionKind"),
            options    = this.getOption("transitionOptions") || {};

        return new Transition(utils.mixin({
            slider: this.slider,
            slide:  slide
        }, options, {
            ignore: true
        }));
    },

    /**
     * Arranges a slide, renders it if needed.
     *
     * @param  {enyo.Control}  slide  The slide
     * @private
     */
    arrangeSlide: function(slide) {
        var slider      = this.slider,
            transition  = slide.sliderTransition || null,
            animating   = transition.animating,
            activeSlide = slider.activeSlide,
            direction   = slider.direction;

        // Pass container dimensions to the transition.
        transition.set("containerWidth", this.containerWidth);
        transition.set("containerHeight", this.containerHeight);

        // Position the slide
        if (animating) {
            transition.apply();
        }
        else if (slide === activeSlide) {
            transition.snapToActive();
        }
        else {
            transition.snapToInactive(direction);
        }
    },

    // Transitions
    // --------------------------------

    /**
     * Removes the slide's node from the DOM and appends it to it's parent, thus
     * putting it on the top of the stacking order.
     *
     * @param {enyo.Control}  slide  The slide
     * @private
     */
    stackSlideOnTop: function(slide) {
        var slider   = this.slider,
            i,
            children = slider.children,
            child,
            z        = 0;

        // Move slide to end of child control stack, ensuring it'll be last.
        children.push(children.splice(children.indexOf(slide), 1)[0]);

        // Apply increasing z-index to each slide except the new one
        for (i = 0; i < children.length; i++) {
            child = children[i];

            if (child.slider === slider && (child instanceof slider.slideKind)) {
                child.applyStyle("z-index", z);
                ++z;
            }
        }
    },

    /**
     * Snaps all current transitions to their final destination.
     *
     * @public
     */
    snapAllTransitions: function() {
        var slides = this.slider.recordUidToSlide,
            slide;

        for (slide in slides) {
            if (slides.hasOwnProperty(slide)) {
                slides[slide].sliderTransition.snap();
            }
        }
    },

    /**
     * Snap to the slide. This method *must* set the `activeSlide` property on
     * the slider as soon as the slide is active.
     *
     * @param {enyo.Control}  slide  The slide
     * @public
     */
    snapTo: function(slide) {
        logger.error("SliderArranger must implement a `snapTo` method", slide);
    },

    /**
     * Initiate a transition to the slide. This method *must* set the
     * `activeSlide` property on the slider as soon as the slide is active.
     *
     * @param {enyo.Control}  slide      The slide
     * @param {number}        index      The index of the slide
     * @param {string}        direction  The direction of the transition
     * @public
     */
    transitionTo: function(slide, index, direction) {
        logger.error("SliderArranger must implement a `transitionTo` method", slide, index, direction);
    }

});

/**
 * This method allows enyo.kind to extend the options hash when creating super
 * kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof SliderArranger
 * @static
 * @private
 */
SliderArranger.concat = function(ctor, props) {
    var proto = ctor.prototype || ctor,
        options;

    if (props.options) {
        options       = proto.options ? utils.clone(proto.options) : {};
        proto.options = utils.mixin(options, props.options);
        delete props.options;
    }
};
